import { captureMonitorMessage } from '@/utils/monitor/core/client'
import { MONITOR_EVENT, MONITOR_PATH, MONITOR_SOURCE, getMonitorDescriptor, getMonitorErrorExtra } from '@/utils/monitor/schema'

// 业务域异常先统一按 15 秒冷却，避免重试或重复动作连续刷屏。
const BUSINESS_ERROR_COOLDOWN_MS = 15000

// business domain 负责业务层的快照失败、动作失败上报。
// 这类 monitor 本身不维护状态，只负责按统一模型组织标签和上下文。
// 参数说明：
// - businessDomain：业务域名称，例如 account / trade / futures。
// - getSymbol：返回当前业务关联的 symbol；没有 symbol 时会退回到 global。
// - isLoggedIn：返回当前是否处于登录态；未登录时直接跳过上报。
// - monitorPath：当前接入方的相对路径，便于在告警里直接定位代码。
// - productType：业务子类型，例如 spot / futures / stock，放在 extra 里辅助筛选。
export const createBusinessMonitor = ({ businessDomain, getSymbol, isLoggedIn, monitorPath, productType }) => {
  const getCurrentSymbol = () => String(getSymbol?.() ?? '')
  const canReport = () => isLoggedIn?.() !== false

  // 统一生成业务监控的核心 tags。
  // 这里只有稳定的四个主字段，避免每个业务域自己再发明一套命名。
  const getBaseTags = (monitorEvent, monitorPoint) => {
    return getMonitorDescriptor({
      monitorSource: MONITOR_SOURCE.BUSINESS,
      businessDomain,
      monitorEvent,
      monitorPath: monitorPath || MONITOR_PATH.DOMAIN_BUSINESS,
      monitorPoint,
    })
  }

  // 统一生成 extra。
  // product_type 和 monitor_symbol 不属于主分类字段，但排查时很有用。
  const getBaseExtra = (extra = {}) => {
    return {
      product_type: productType,
      monitor_symbol: getCurrentSymbol(),
      ...extra,
    }
  }

  // 快照类问题，例如列表拉取失败、账户资产获取失败。
  const reportSnapshotFailure = (resource, error, extra = {}) => {
    if (!canReport()) {
      return
    }

    // 没有 symbol 的全局业务问题，统一归到 global，避免 dedupe key 为空。
    const symbol = getCurrentSymbol() || 'global'
    const monitor = getBaseTags(MONITOR_EVENT.SNAPSHOT_FAILURE, resource)

    captureMonitorMessage(monitor.title, {
      payload: error,
      level: 'error',
      dedupeKey: `business:snapshot_failure:${businessDomain}:${symbol}:${resource}`,
      cooldownMs: BUSINESS_ERROR_COOLDOWN_MS,
      fingerprint: [monitor.title, resource, monitorPath || MONITOR_PATH.DOMAIN_BUSINESS],
      tags: monitor.tags,
      extra: getMonitorErrorExtra(error, getBaseExtra({
        resource,
        ...extra,
      })),
    })
  }

  // 动作类问题，例如撤单失败、批量操作失败。
  const reportActionFailure = (action, error, extra = {}) => {
    if (!canReport()) {
      return
    }

    // 动作类问题同样按业务域 + symbol + action 去重。
    const symbol = getCurrentSymbol() || 'global'
    const monitor = getBaseTags(MONITOR_EVENT.ACTION_FAILURE, action)

    captureMonitorMessage(monitor.title, {
      payload: error,
      level: 'error',
      dedupeKey: `business:action_failure:${businessDomain}:${symbol}:${action}`,
      cooldownMs: BUSINESS_ERROR_COOLDOWN_MS,
      fingerprint: [monitor.title, action, monitorPath || MONITOR_PATH.DOMAIN_BUSINESS],
      tags: monitor.tags,
      extra: getMonitorErrorExtra(error, getBaseExtra({
        action,
        ...extra,
      })),
    })
  }

  return {
    reportSnapshotFailure,
    reportActionFailure,
  }
}
